import { useEffect, useState } from 'react';
import { apiClient } from '@/services/apiClient';

const HEARTBEAT_MS = 15_000;

export interface Connectivity {
  /** What the browser thinks. Necessary but not sufficient. */
  isBrowserOnline: boolean;
  /** Whether the API answered the last heartbeat. */
  isServerReachable: boolean;
  /** Both. The only thing the counter should gate on. */
  isOnline: boolean;
}

/**
 * Whether the till can trust the server right now.
 *
 * `navigator.onLine` alone is not enough: the interesting failure at a shop counter is a router
 * that is up while the line behind it is down, which the browser cheerfully reports as online. So a
 * heartbeat against the health endpoint decides, and the browser flag only shortcuts the obvious
 * case and triggers an immediate re-check when the cable goes back in.
 */
export function useConnectivity(): Connectivity {
  const [isBrowserOnline, setBrowserOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );
  const [isServerReachable, setServerReachable] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        if (!cancelled) setServerReachable(false);
        return;
      }

      try {
        await apiClient.get('/health', { timeout: 5_000 });
        if (!cancelled) setServerReachable(true);
      } catch {
        if (!cancelled) setServerReachable(false);
      }
    };

    const onOnline = () => {
      setBrowserOnline(true);
      void check();
    };
    const onOffline = () => {
      setBrowserOnline(false);
      setServerReachable(false);
    };

    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    void check();
    const timer = window.setInterval(() => void check(), HEARTBEAT_MS);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  return {
    isBrowserOnline,
    isServerReachable,
    isOnline: isBrowserOnline && isServerReachable,
  };
}
